import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useSession } from "../lib/session";
import { Icon } from "../ui/Icon";
import { Avatar } from "../ui/Avatar";
import { BrandMark } from "../ui/Brand";
import { visibleNav, type NavItem } from "./nav";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { badgeCounts, useHome } from "../features/home/api";
import { useT } from "../lib/i18n";
import { endSupportAndReturn } from "../lib/support";
import { SupportBanner } from "./SupportBanner";

function NavEntry({ item, count, onNavigate }: { item: NavItem; count?: number; onNavigate?: () => void }) {
  return (
    <NavLink to={item.to} end={item.to === "/"} className={({ isActive }) => `nav__link${isActive ? " is-active" : ""}`} onClick={onNavigate}>
      <Icon name={item.icon} size={20} />
      <span className="nav__label">{item.label}</span>
      {count ? (
        <span className="nav__badge" aria-label={`${count} bekleyen`}>
          {count}
        </span>
      ) : null}
    </NavLink>
  );
}

/** Uygulama iskeleti: masaüstünde kenar çubuğu, telefonda alt sekme çubuğu + "Diğer" çekmecesi. */
export function AppShell() {
  const { session, can, isManager, logout: plainLogout } = useSession();
  const t = useT();
  const support = session?.support ?? null;
  // Destek oturumunda "Çıkış yap" yalnız destek oturumunu bitirir ve panele döner.
  const logout = support ? endSupportAndReturn : plainLogout;
  const logoutLabel = support ? t("platform.banner.end") : "Çıkış yap";
  const home = useHome(Boolean(session));
  // Okunmamış bildirim sayısı: dakikada bir ve pencereye dönünce tazelenir (bildirim sayfası okundu işaretleyince geçersizlenir).
  const unread = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => get<{ unreadCount: number }>("/notifications/unread-count"),
    enabled: Boolean(session),
    refetchInterval: 60_000,
  });
  const counts = { ...badgeCounts(home.data), notifications: unread.data?.unreadCount ?? 0 };
  const nav = visibleNav({ can, isManager, hasEmployee: Boolean(session?.employee), hasApprovals: counts.approvals > 0 || isManager || can("leave.manage") || can("expenses.finance") });
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);

  // Sayfa değişince odak ana içeriğe taşınır (ekran okuyucular yeni sayfayı duyurur).
  useEffect(() => {
    setMoreOpen(false);
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!session) return null;
  const tabs = nav.flatMap((g) => g.items).filter((i) => i.tab);
  const countFor = (i: NavItem) => (i.badge ? counts[i.badge] : 0);

  return (
    <div className={`shell${support ? " shell--support" : ""}`}>
      <a href="#main" className="skip-link">
        İçeriğe geç
      </a>
      {support && <SupportBanner support={support} tenantName={session.tenant.name} viewerName={session.employee?.name ?? session.user.displayName} />}
      <aside className="sidebar" aria-label="Ana menü">
        <div className="sidebar__brand">
          <BrandMark size={28} />
          <div>
            <p className="sidebar__company">{session.tenant.name}</p>
            {session.otherTenants.length > 0 && (
              <button type="button" className="sidebar__switch" onClick={() => navigate("/profil#sirketler")}>
                Şirket değiştir
              </button>
            )}
          </div>
        </div>
        <nav className="nav">
          {nav.map((g, gi) => (
            <div key={gi} className="nav__group">
              {g.label && <p className="nav__group-label">{g.label}</p>}
              {g.items.map((i) => (
                <NavEntry key={i.to} item={i} count={countFor(i)} />
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar__me">
          <NavLink to="/profil" className="me">
            <Avatar initials={session.employee?.initials ?? session.user.displayName.slice(0, 2).toLocaleUpperCase("tr-TR")} size={32} />
            <span className="me__text">
              <span className="me__name">{session.employee?.name ?? session.user.displayName}</span>
              {session.employee?.title && <span className="me__title">{session.employee.title}</span>}
            </span>
          </NavLink>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void logout()}>
            <Icon name="sign-out" size={16} />
            <span>{logoutLabel}</span>
          </button>
        </div>
      </aside>

      <div className="shell__main">
        <header className="topbar">
          <div className="topbar__brand">
            <BrandMark size={24} />
            <span className="topbar__company">{session.tenant.name}</span>
          </div>
          <div className="topbar__actions">
            <NavLink to="/bildirimler" className="icon-btn topbar__bell" aria-label={counts.notifications ? `Bildirimler, ${counts.notifications} okunmamış` : "Bildirimler"}>
              <Icon name="bell" size={22} />
              {counts.notifications > 0 && (
                <span className="topbar__badge" aria-hidden="true">
                  {counts.notifications > 99 ? "99+" : counts.notifications}
                </span>
              )}
            </NavLink>
          </div>
        </header>
        <main id="main" ref={mainRef} tabIndex={-1} className="main">
          <Outlet />
        </main>
      </div>

      <nav className="tabbar" aria-label="Hızlı erişim">
        {tabs.slice(0, 4).map((i) => (
          <NavLink key={i.to} to={i.to} end={i.to === "/"} className={({ isActive }) => `tabbar__item${isActive ? " is-active" : ""}`}>
            <span className="tabbar__icon">
              <Icon name={i.icon} size={24} />
              {countFor(i) ? <span className="tabbar__badge">{countFor(i)}</span> : null}
            </span>
            <span className="tabbar__label">{i.label === "İzinlerim" ? "İzin" : i.label === "Belgelerim" ? "Belgeler" : i.label}</span>
          </NavLink>
        ))}
        <button type="button" className={`tabbar__item${moreOpen ? " is-active" : ""}`} aria-expanded={moreOpen} aria-controls="more-sheet" onClick={() => setMoreOpen((o) => !o)}>
          <span className="tabbar__icon">
            <Icon name="list" size={24} />
          </span>
          <span className="tabbar__label">Diğer</span>
        </button>
      </nav>
      {moreOpen && (
        <div className="sheet-backdrop" onClick={() => setMoreOpen(false)}>
          <div id="more-sheet" className="sheet" role="dialog" aria-label="Tüm bölümler" onClick={(e) => e.stopPropagation()}>
            <nav className="nav">
              {nav.map((g, gi) => (
                <div key={gi} className="nav__group">
                  {g.label && <p className="nav__group-label">{g.label}</p>}
                  {g.items
                    .filter((i) => !i.tab)
                    .map((i) => (
                      <NavEntry key={i.to} item={i} count={countFor(i)} onNavigate={() => setMoreOpen(false)} />
                    ))}
                </div>
              ))}
              <div className="nav__group">
                <NavLink to="/profil" className="nav__link">
                  <Icon name="user" size={20} />
                  <span className="nav__label">Profilim</span>
                </NavLink>
                <button type="button" className="nav__link" onClick={() => void logout()}>
                  <Icon name="sign-out" size={20} />
                  <span className="nav__label">{logoutLabel}</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
